// Running line / marquee ("LET`S GET STARTED" + lime icon). Feeds each
// .section-marquee__row's items into the horizontalLoop helper below for an infinite, responsive loop.
import { gsap } from 'gsap';

export default function initMarquee() {
  const rows = gsap.utils.toArray('.section-marquee__row');
  if (!rows.length) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // Per-row state: the current timeline + its scroll direction. Hover
  // handlers always read `state.tl`, so they track the live timeline across rebuilds.
  const states = rows.map((row, i) => ({
    row,
    items: row.querySelectorAll('.section-marquee__item, .section-marquee__icon'),
    dir: i % 2 === 1 ? -1 : 1, // row 0 → left, row 1 → right, …
    reversed: i % 2 === 1,
    tl: null,
  }));

  // Tear the loop down to the element's natural (static) state, so nothing
  // is left frozen in an overlapped position while the user resizes.
  const reset = (s) => {
    if (s.tl) {
      s.tl.kill();
      s.tl = null;
    }
    // horizontalLoop sets inline transforms (xPercent/x) on every item; clear
    // them so widths/offsets measure cleanly from the natural flex flow.
    gsap.set(s.items, { clearProps: 'all' });
  };

  const buildRow = (s) => {
    if (s.items.length < 2) return;
    s.tl = horizontalLoop(s.items, {
      repeat: -1,
      speed: 1,
      reversed: s.reversed,
      paddingRight: 30,
    });
  };

  const buildAll = () => states.forEach((s) => buildRow(s));
  const resetAll = () => states.forEach((s) => reset(s));

  const start = () => {
    buildAll();

    // Hover pause — bound once, reads the current timeline so it survives rebuilds.
    states.forEach((s) => {
      s.row.addEventListener('mouseenter', () => {
        if (s.tl) gsap.to(s.tl, { timeScale: 0, overwrite: true });
      });
      s.row.addEventListener('mouseleave', () => {
        if (s.tl) gsap.to(s.tl, { timeScale: s.dir, overwrite: true });
      });
    });

    // Rebuild the seamless loop after a width change: item sizing uses vw-based
    // clamp(), so without rebuilding the loop geometry drifts and rows overlap.
    let resizeTimer;
    let resizing = false;
    let lastWidth = window.innerWidth;

    window.addEventListener('resize', () => {
      // Ignore height-only changes (e.g. mobile browser chrome show/hide).
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;

      if (!resizing) {
        resizing = true;
        resetAll();
      }

      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        resizing = false;
        resetAll(); // ensure a clean slate, then rebuild against the new widths
        buildAll();
      }, 200);
    });
  };

  // Wait for the heading webfont (Montserrat) so item widths are measured at
  // their final size — otherwise spacing drifts once the webfont swaps in.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(start);
  } else {
    start();
  }
}

// GSAP `horizontalLoop` helper (https://codepen.io/GreenSock/pen/jOBBVjr),
// copied verbatim: animates a group of elements in a seamless, responsive x-axis loop.
function horizontalLoop(items, config) {
  items = gsap.utils.toArray(items);
  config = config || {};
  let tl = gsap.timeline({ repeat: config.repeat, paused: config.paused, defaults: { ease: 'none' }, onReverseComplete: () => tl.totalTime(tl.rawTime() + tl.duration() * 100) }),
    length = items.length,
    startX = items[0].offsetLeft,
    times = [],
    widths = [],
    xPercents = [],
    curIndex = 0,
    pixelsPerSecond = (config.speed || 1) * 100,
    snap = config.snap === false ? (v) => v : gsap.utils.snap(config.snap || 1),
    totalWidth, curX, distanceToStart, distanceToLoop, item, i;
  gsap.set(items, {
    // convert "x" to "xPercent" to make things responsive, and populate the widths/xPercents Arrays to make lookups faster.
    xPercent: (i, el) => {
      let w = (widths[i] = parseFloat(gsap.getProperty(el, 'width', 'px')));
      xPercents[i] = snap((parseFloat(gsap.getProperty(el, 'x', 'px')) / w) * 100 + gsap.getProperty(el, 'xPercent'));
      return xPercents[i];
    },
  });
  gsap.set(items, { x: 0 });
  totalWidth = items[length - 1].offsetLeft + (xPercents[length - 1] / 100) * widths[length - 1] - startX + items[length - 1].offsetWidth * gsap.getProperty(items[length - 1], 'scaleX') + (parseFloat(config.paddingRight) || 0);
  for (i = 0; i < length; i++) {
    item = items[i];
    curX = (xPercents[i] / 100) * widths[i];
    distanceToStart = item.offsetLeft + curX - startX;
    distanceToLoop = distanceToStart + widths[i] * gsap.getProperty(item, 'scaleX');
    tl.to(item, { xPercent: snap(((curX - distanceToLoop) / widths[i]) * 100), duration: distanceToLoop / pixelsPerSecond }, 0)
      .fromTo(item, { xPercent: snap(((curX - distanceToLoop + totalWidth) / widths[i]) * 100) }, { xPercent: xPercents[i], duration: (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond, immediateRender: false }, distanceToLoop / pixelsPerSecond)
      .add('label' + i, distanceToStart / pixelsPerSecond);
    times[i] = distanceToStart / pixelsPerSecond;
  }
  function toIndex(index, vars) {
    vars = vars || {};
    Math.abs(index - curIndex) > length / 2 && (index += index > curIndex ? -length : length); // always go in the shortest direction
    let newIndex = gsap.utils.wrap(0, length, index),
      time = times[newIndex];
    if (time > tl.time() !== index > curIndex) {
      // if we're wrapping the timeline's playhead, make the proper adjustments
      vars.modifiers = { time: gsap.utils.wrap(0, tl.duration()) };
      time += tl.duration() * (index > curIndex ? 1 : -1);
    }
    curIndex = newIndex;
    vars.overwrite = true;
    return tl.tweenTo(time, vars);
  }
  tl.next = (vars) => toIndex(curIndex + 1, vars);
  tl.previous = (vars) => toIndex(curIndex - 1, vars);
  tl.current = () => curIndex;
  tl.toIndex = (index, vars) => toIndex(index, vars);
  tl.times = times;
  if (config.reversed) {
    tl.vars.onReverseComplete();
    tl.reverse();
  }
  return tl;
}
